// src/context/NotificationContext.js
import React, { createContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid'; // For generating unique IDs

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [snackbar, setSnackbar] = useState(null); // New state for the snackbar

  const addNotification = useCallback(
    (
      message,
      type = 'info',
      duration = null, // Changed default from 3000 to null
      isPersistent = false, // New: For persistent notifications
      actions = [] // New: Array of action objects { text: 'Click Me', onClick: () => {} }
    ) => {
      const id = uuidv4();
      // If it's a non-persistent success message, send it to the snackbar
      if (type === 'success' && !isPersistent) {
        setSnackbar({ id, message, duration: duration || 3000 }); // Use passed duration or default
        return;
      }

      // Otherwise, handle it as a regular notification
      setNotifications((prevNotifications) => [...prevNotifications, { id, message, type, duration: isPersistent ? 0 : duration, isPersistent, actions }]);
    },
    []
  );

  const removeNotification = useCallback((id) => {
    setNotifications((prevNotifications) => prevNotifications.filter((notification) => notification.id !== id));
  }, []);

  // New function to clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // New function to clear the snackbar
  const clearSnackbar = useCallback(() => {
    setSnackbar(null);
  }, []);

  // This function can be used by the GlobalNotificationArea to auto-remove after duration
  const dismissNotification = useCallback(
    (id) => {
      // This is essentially the same as removeNotification for now,
      // but could be extended if needed (e.g., for animations before removal)
      removeNotification(id);
    },
    [removeNotification]
  );

  const contextValue = {
    notifications,
    snackbar, // Expose snackbar state
    addNotification,
    removeNotification, // Kept for manual dismissal if ever needed
    dismissNotification,
    clearSnackbar, // Expose clear function
    clearAllNotifications, // Expose the new function
  };

  return <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>;
};
